import { SECTION_HERO_XML } from '../../../shared/onboardingSectionArt.js'
import { ANNOUNCEMENT_ICON_XML as ICON } from '../../../shared/announcementIcons.js'
import { HERO_MOMENT_XML } from '../../../shared/heroMomentArt.js'
import Seo from '../components/Seo'
import MarketingNav from '../components/MarketingNav'
import MarketingFooter from '../components/MarketingFooter'
import PageHero from '../components/ds/PageHero'
import Section from '../components/ds/Section'
import SectionHead, { H2 } from '../components/ds/SectionHead'
import CtaBand from '../components/ds/CtaBand'
import Button from '../components/ds/Button'
import Art from '../components/ds/Art'
import Prose from '../components/ds/Prose'
import NotAnAgency from '../components/ds/NotAnAgency'
import FaqAccordion from '../components/ds/FaqAccordion'
import { getCoverJsonLd } from '../lib/landingJsonLd'
import { RATE } from '../content/pricing'
import { dashboardUrl } from '../lib/dashboardUrl'

// /business/pricing — the page that beats an agency, and the one the old site
// buried in a section.
//
// ⚠️ THE HEADLINE NUMBER IS A FOUNDER DECISION, not a copy decision. It comes
// from src/content/pricing.js and is null until somebody chooses to publish it.
// With it null this page still does its job: it says plainly what you pay for,
// what you do not, and that you see the cost before you commit, which is true
// today. No invented figure, no "from £X", no "contact us".

const INCLUDED = [
  'Every check we run on the person who turns up',
  'Their pay, handled by us, with nothing for you to process',
  'Cover for the shift if something goes wrong on the day',
  'A record of every shift, exportable whenever you need it',
]

const NOT_CHARGED = [
  'Joining, and keeping an account open',
  'Posting a shift, however many you post',
  'A shift nobody accepts',
  'Keeping a list of the people you want back',
]

const FAQS = [
  { q: 'Is there a subscription?',
    a: "No. There’s nothing to pay to have an account, and nothing to pay to post a shift." },
  { q: 'What happens if no one accepts?',
    a: "You pay nothing. You’re charged for cover you actually got." },
  { q: 'Do we pay the professional directly?',
    a: "No, and that’s deliberate. We handle their pay, so there’s no invoice to process and nothing for your team to chase." },
  { q: 'What if the shift runs over?',
    a: "You see the extra time and you approve it before it’s charged." },
  { q: 'How do we pay?',
    a: 'By card or Direct Debit, after the shift, on one bill.' },
]

export default function CoverPricing() {
  return (
    <div className="bg-canvas">
      <Seo
        title="What locum cover costs"
        description="You see the full cost of a shift before you post it. No agency margin, nothing to join, nothing to post, and nothing to pay for a shift no one accepts."
        path="/business/pricing"
        jsonLd={getCoverJsonLd(FAQS, [['Get cover', '/business'], ['Pricing', '/business/pricing']])}
      />
      <MarketingNav />

      {/* ⚠️ The ONE main landmark. Without it every <section> on the page
          sits outside any landmark and a screen reader user navigating by
          landmark cannot reach the content at all. The skip link in
          MarketingNav targets this id. */}
      <main id="main" tabIndex={-1} className="outline-none">

      <PageHero
        art={SECTION_HERO_XML.paying}
        eyebrow="Get cover"
        title={RATE ? <>{RATE.headline}. That’s the whole of it.</> : <>You see the price before you post.</>}
        lead={RATE
          ? RATE.note
          : 'The full cost of a shift is in front of you before you commit to it. Not after a phone call, not after the day, and with no agency margin inside it.'}
      >
        <Button href={dashboardUrl('/register')}>Post a shift</Button>
      </PageHero>

      <Section ground="canvas">
        <div className="md:flex md:gap-20">
          <div className="flex-1">
            <SectionHead eyebrow="What you pay for" size="md" title={<>Cover that turned up.</>} />
            <ul className="mt-7 space-y-4">
              {INCLUDED.map((i) => (
                <li key={i} className="flex gap-4 border-b border-hairline pb-4 text-[18px]">
                  <span aria-hidden="true" className="mt-1.5 shrink-0 text-action">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l6 6L20 6" /></svg>
                  </span>
                  {i}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-12 flex-1 md:mt-0">
            <SectionHead eyebrow="What you do not pay for" size="md" title={<>Everything else.</>} />
            <ul className="mt-7 space-y-4">
              {NOT_CHARGED.map((i) => (
                <li key={i} className="border-b border-hairline pb-4 text-[18px] text-ink-muted">{i}</li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section ground="navy">
        <div className="md:flex md:items-center md:gap-16">
          <Art xml={HERO_MOMENT_XML.ratesMatched} className="h-[150px] w-[200px] shrink-0" />
          <div className="mt-8 md:mt-0">
            <H2 size="md">
              Every pound you add to a rate goes to the person working.
            </H2>
            <p className="mt-4 max-w-[640px] text-[19px] leading-relaxed text-white/75">
              If you raise what a shift pays to fill it, that’s not a bigger margin for us. It’s
              a better day for them, which is why they take it.
            </p>
          </div>
        </div>
      </Section>

      <NotAnAgency ground="canvas" />

      <Section ground="surface" width="text">
        <div className="space-y-12">
          <Prose id="when" art={ICON.payday} title="When you pay">
            <p>
              After the shift, not before it. Hours come from their own clock in and clock out, so
              you are charged for the time that was actually worked.
            </p>
            <p>
              You have a day afterwards to raise anything that does not look right.
            </p>
          </Prose>

          <Prose id="how" art={ICON.wallet} title="How you pay">
            <p>
              Card or Direct Debit, on one bill, with every shift itemised. There’s no invoice
              from the professional to process and nothing to run through payroll, because they
              are self employed and we handle their pay.
            </p>
          </Prose>

          <Prose id="rates" art={ICON.rate} title="What a shift should pay">
            <p>
              You set it. We show you what similar shifts nearby are going for so you are not
              guessing, and if nothing is moving we tell you what would change that rather than
              leaving the gap open.
            </p>
          </Prose>
        </div>
      </Section>

      <Section ground="surface" width="text">
        <H2>Questions, answered.</H2>
        <FaqAccordion items={FAQS} className="mt-10" />
      </Section>

      <CtaBand
        title="See the cost on a real shift."
        lead="Set up an account and post one. Nothing is charged until somebody works it."
      >
        <Button href={dashboardUrl('/register')}>Get started</Button>
      </CtaBand>

      </main>

      <MarketingFooter />
    </div>
  )
}
